/**
 * About Page - Personal story and platform mission
 */

import { Target, BarChart3, ExternalLink, Award, Zap, Users, TrendingUp, Heart } from 'lucide-react';
import { PageLayout, PageHeader, Section, Grid } from '../components';
import { ListingCard } from '../components/listing/v2';
import { useTheme } from '../contexts/ThemeContext';
import styles from './About.module.css';

const About = () => {
  const { theme } = useTheme();

  return (
    <PageLayout maxWidth="1400px" className="about-page">
      <PageHeader
        title="Our Story"
        subtitle="Built by passion, driven by purpose"
      />

      <Section>
        <div className={styles.storyGrid}>
        {/* Purpose Card */}
        <ListingCard
          title="Our Purpose"
          icon={<Target size={40} />}
          align="left"
          size="large"
          iconVariant="primary"
          className={styles.optionCard}
        >
          <p className={styles.topDescription}>
            The World’s Pitch Initiative exists to remove financial barriers in youth soccer. Our mission is to make soccer accessible and affordable for young players, particularly at the elementary and middle school levels, by providing quality development in an inclusive environment. We aim to help players find joy in the sport, build strong fundamentals, and prepare for competitive opportunities.
          </p>
        </ListingCard>

        {/* Cost Statistics Card */}
        <ListingCard
          title="Cost Statistics"
          icon={<BarChart3 size={40} />}
          align="left"
          size="large"
          iconVariant="secondary"
          className={styles.optionCard}
        >
          <div className={styles.topDescription}>
            Youth soccer can be expensive. On average, private training in the U.S. ranges from <span style={{ color: theme.colors.primary, fontWeight: 'bold' }}>$40</span> to <span style={{ color: theme.colors.primary, fontWeight: 'bold' }}>$100+</span> per hour.

            <div className={styles.statLinksContainer}>
              <a
                href="https://teachme.to/blog/how-much-do-soccer-lessons-cost"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.statLink}
              >
                Average Cost of Soccer Lessons <ExternalLink size={14} />
              </a>
              <a
                href="https://ollyontheball.com/private-soccer-lessons-cost"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.statLink}
              >
                Cost of Private Soccer Lessons <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </ListingCard>
      </div>
      </Section>

      {/* Our Approach Heading */}
      <Section>
        <div className={styles.sectionTitle}>Our Approach</div>

        {/* Approach Grid */}
        <Grid
        minColumnWidth="280px"
        className={styles.approachGrid}
        gap="2rem"
      >
        {/* Premier Coaching */}
        <ListingCard
          title="Premier Coaching"
          icon={<Award size={40} />}
          iconVariant="success"
          size="large"
          align="center"
          subtitle="Our coaches play at some of the highest level youth soccer in the U.S., sharing top-level feedback and developmental strategies."
        />

        {/* Skill Development */}
        <ListingCard
          title="Skill Development"
          icon={<Zap size={40} />}
          iconVariant="primary"
          size="large"
          align="center"
          subtitle="Progressive training that builds technical skills appropriate for each age group, focusing on fundamentals."
        />

        {/* Character Building */}
        <ListingCard
          title="Character Building"
          icon={<Users size={40} />}
          iconVariant="success"
          size="large"
          align="center"
          subtitle="Teaching teamwork, perseverance, and sportsmanship, developing well-rounded young athletes."
        />

        {/* Individual Growth */}
        <ListingCard
          title="Individual Growth"
          icon={<TrendingUp size={40} />}
          iconVariant="secondary"
          size="large"
          align="center"
          subtitle="Recognizing that everyone develops at their own pace and celebrating progress, ensuring no one is left behind."
        />

        {/* Love of the Game */}
        <ListingCard
          title="Love of the Game"
          icon={<Heart size={40} />}
          iconVariant="primary"
          size="large"
          align="center"
          subtitle="Keeping soccer fun and engaging while building competitive skills, because passion is the foundation."
        />
      </Grid>
      </Section>
    </PageLayout>
  );
};

export default About;
